import { supabase } from '../lib/supabase';

// Generate random 6-digit verification code
const generateVerificationCode = () => {
  return Math.floor(100000 + Math.random() * 900000)?.toString();
};

export const verificationService = {
  // Send email verification code
  // Accepts optional userId so it works immediately after signup (no session yet)
  async sendEmailVerification(email, userId = null) {
    try {
      let resolvedUserId = userId;

      if (!resolvedUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) throw new Error('Not authenticated');
        resolvedUserId = user?.id;
      }

      const code = generateVerificationCode();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes

      // Store verification code
      const { data, error } = await supabase?.from('user_verifications')?.insert({
          user_id: resolvedUserId,
          verification_type: 'email',
          contact_value: email,
          verification_code: code,
          expires_at: expiresAt?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      // Send verification email via edge function
      try {
        const { error: emailError } = await supabase?.functions?.invoke('send-transactional-email', {
          body: {
            to: email,
            type: 'verification_code',
            data: { code }
          }
        });

        if (emailError) {
          console.error('Email sending failed:', emailError);
          // Don't throw - verification code is still valid
        }
      } catch (emailErr) {
        console.error('Email service error:', emailErr);
        // Continue - verification code is stored and valid
      }

      return { success: true, verificationId: data?.id };
    } catch (error) {
      console.error('Error sending email verification:', error);
      throw error;
    }
  },

  // Send SMS verification code via Twilio
  async sendSmsVerification(phoneNumber, userId = null) {
    try {
      let resolvedUserId = userId;

      if (!resolvedUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) throw new Error('Not authenticated');
        resolvedUserId = user?.id;
      }

      const code = generateVerificationCode();
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000); // 15 minutes

      // Store verification code
      const { data: verificationData, error: dbError } = await supabase?.from('user_verifications')?.insert({
          user_id: resolvedUserId,
          verification_type: 'sms',
          contact_value: phoneNumber,
          verification_code: code,
          expires_at: expiresAt?.toISOString()
        })?.select()?.single();

      if (dbError) throw dbError;

      // Send SMS via Twilio Edge Function
      const { error: twilioError } = await supabase?.functions?.invoke('send-twilio-sms', {
        body: {
          to: phoneNumber,
          message: `Your GrowGuardian verification code is: ${code}. Valid for 15 minutes.`
        }
      });

      if (twilioError) {
        await supabase?.from('user_verifications')?.update({ status: 'failed' })?.eq('id', verificationData?.id);
        throw twilioError;
      }

      return { success: true, verificationId: verificationData?.id };
    } catch (error) {
      console.error('Error sending SMS verification:', error);
      throw error;
    }
  },

  // Verify code
  // Accepts optional userId so it works without an active session
  async verifyCode(verificationId, code, userId = null) {
    try {
      let resolvedUserId = userId;

      if (!resolvedUserId) {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) throw new Error('Not authenticated');
        resolvedUserId = user?.id;
      }

      // Get verification record
      const { data: verification, error: fetchError } = await supabase
        ?.from('user_verifications')
        ?.select('*')
        ?.eq('id', verificationId)
        ?.eq('user_id', resolvedUserId)
        ?.single();

      if (fetchError) throw fetchError;
      if (!verification) throw new Error('Verification not found');

      // Check if expired
      if (new Date(verification.expires_at) < new Date()) {
        await supabase?.from('user_verifications')?.update({ status: 'expired' })?.eq('id', verificationId);
        throw new Error('Verification code has expired');
      }

      // Check max attempts
      if (verification?.attempts >= verification?.max_attempts) {
        await supabase?.from('user_verifications')?.update({ status: 'failed' })?.eq('id', verificationId);
        throw new Error('Maximum verification attempts exceeded');
      }

      // Check code match
      if (verification?.verification_code !== code) {
        await supabase?.from('user_verifications')?.update({ attempts: verification?.attempts + 1 })?.eq('id', verificationId);
        throw new Error('Invalid verification code');
      }

      // Mark as verified
      const { error: updateError } = await supabase?.from('user_verifications')?.update({
          status: 'verified',
          verified_at: new Date()?.toISOString()
        })?.eq('id', verificationId);

      if (updateError) throw updateError;

      return { success: true, verificationType: verification?.verification_type };
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  },

  // Check verification status
  async getVerificationStatus(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('email_verified, phone_verified, email_verified_at, phone_verified_at')?.eq('id', userId)?.single();

      if (error) throw error;

      return {
        emailVerified: data?.email_verified || false,
        phoneVerified: data?.phone_verified || false,
        emailVerifiedAt: data?.email_verified_at,
        phoneVerifiedAt: data?.phone_verified_at
      };
    } catch (error) {
      console.error('Error getting verification status:', error);
      throw error;
    }
  }
};